import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import authService from '../services/authService';
import QRScanner from './QRScanner';

const Register = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        role: 'customer',
        restaurantName: '',
        phone: ''
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [showScanner, setShowScanner] = useState(false);
    const navigate = useNavigate();

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        
        // Validate passwords match
        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            setIsLoading(false);
            return;
        }
        
        try {
            if (formData.role === 'customer') {
                // Customer registration with phone number
                const customerData = {
                    name: formData.name,
                    phoneNumber: formData.phone,
                    password: formData.password
                };
                await authService.registerCustomer(customerData);
            } else {
                // Restaurant registration with email
                const userData = {
                    name: formData.name,
                    email: formData.email,
                    password: formData.password,
                    role: 'restaurantadmin'
                };
                await authService.register(userData);
            }
            
            navigate('/login');
        } catch (error) {
            console.error('Registration failed:', error);
            setError(error.message || 'Registration failed. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleQRScan = (data) => {
        // Navigate to customer menu with scanned restaurant and table info
        const { restaurantId, tableId } = data;
        setShowScanner(false);
        navigate(`/menu/${restaurantId}/${tableId}`);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center px-4 py-8">
            <div className="max-w-md w-full">
                {/* Logo and Header */}
                <div className="text-center mb-8">
                    <img src="/logo-bright.svg" alt="RestroAI" className="h-16 w-auto mx-auto mb-6" />
                    <h2 className="text-3xl font-bold text-white mb-2">Join RestroAI</h2>
                    <p className="text-white/70">Create your account and transform your restaurant</p>
                </div>

                {/* Registration Form */}
                <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 border border-white/20 shadow-2xl">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Error Message */}
                        {error && (
                            <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-4 mb-4">
                                <div className="flex items-center space-x-2">
                                    <span className="text-red-400">⚠️</span>
                                    <span className="text-red-200 text-sm">{error}</span>
                                </div>
                            </div>
                        )}
                        
                        {/* Role Selection */}
                        <div>
                            <label className="block text-sm font-semibold text-white mb-3">
                                I am a:
                            </label>
                            <div className="grid grid-cols-2 gap-3">
                                <label className="relative">
                                    <input
                                        type="radio"
                                        name="role"
                                        value="customer"
                                        checked={formData.role === 'customer'}
                                        onChange={handleChange}
                                        className="sr-only"
                                    />
                                    <div className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 ${
                                        formData.role === 'customer' 
                                            ? 'border-blue-500 bg-blue-500/20' 
                                            : 'border-white/20 bg-white/5 hover:bg-white/10'
                                    }`}>
                                        <div className="text-center">
                                            <div className="text-2xl mb-2">👤</div>
                                            <div className="text-white font-medium">Customer</div>
                                            <div className="text-white/60 text-xs">Order food & track orders</div>
                                        </div>
                                    </div>
                                </label>
                                <label className="relative">
                                    <input
                                        type="radio"
                                        name="role"
                                        value="restaurant"
                                        checked={formData.role === 'restaurant'}
                                        onChange={handleChange}
                                        className="sr-only"
                                    />
                                    <div className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 ${
                                        formData.role === 'restaurant' 
                                            ? 'border-blue-500 bg-blue-500/20' 
                                            : 'border-white/20 bg-white/5 hover:bg-white/10'
                                    }`}>
                                        <div className="text-center">
                                            <div className="text-2xl mb-2">🏪</div>
                                            <div className="text-white font-medium">Restaurant</div>
                                            <div className="text-white/60 text-xs">Manage your business</div>
                                        </div>
                                    </div>
                                </label>
                            </div>
                        </div>

                        {/* Name */}
                        <div>
                            <label htmlFor="name" className="block text-sm font-semibold text-white mb-2">
                                {formData.role === 'restaurant' ? 'Restaurant Name' : 'Full Name'}
                            </label>
                            <input
                                id="name"
                                name="name"
                                type="text"
                                placeholder={formData.role === 'restaurant' ? 'Enter restaurant name' : 'Enter your full name'}
                                value={formData.name}
                                onChange={handleChange}
                                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                                required
                            />
                        </div>

                        {/* Email (for restaurants only) */}
                        {formData.role === 'restaurant' && (
                            <div>
                                <label htmlFor="email" className="block text-sm font-semibold text-white mb-2">
                                    Email Address
                                </label>
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    placeholder="Enter your email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                                    required
                                />
                            </div>
                        )}

                        {/* Phone Number */}
                        <div>
                            <label htmlFor="phone" className="block text-sm font-semibold text-white mb-2">
                                Phone Number
                            </label>
                            <input
                                id="phone"
                                name="phone"
                                type="tel"
                                placeholder="Enter 10-digit phone number"
                                value={formData.phone}
                                onChange={handleChange}
                                pattern="[0-9]{10}"
                                maxLength="10"
                                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                                required
                            />
                        </div>

                        {/* Password */}
                        <div>
                            <label htmlFor="password" className="block text-sm font-semibold text-white mb-2">
                                Password
                            </label>
                            <input
                                id="password"
                                name="password"
                                type="password"
                                placeholder="Create a password"
                                value={formData.password}
                                onChange={handleChange}
                                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                                required
                            />
                        </div>

                        {/* Confirm Password */}
                        <div>
                            <label htmlFor="confirmPassword" className="block text-sm font-semibold text-white mb-2">
                                Confirm Password
                            </label>
                            <input
                                id="confirmPassword"
                                name="confirmPassword"
                                type="password"
                                placeholder="Confirm your password"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                                required
                            />
                        </div>

                        {/* Terms and Conditions */}
                        <div className="flex items-start">
                            <input
                                type="checkbox"
                                id="terms"
                                className="mt-1 rounded border-white/20 bg-white/10 text-blue-500 focus:ring-blue-500"
                                required
                            />
                            <label htmlFor="terms" className="ml-2 text-sm text-white/70">
                                I agree to the{' '}
                                <Link to="/terms-of-service" className="text-blue-400 hover:text-blue-300 transition-colors">
                                    Terms of Service
                                </Link>{' '}
                                and{' '}
                                <Link to="/privacy-policy" className="text-blue-400 hover:text-blue-300 transition-colors">
                                    Privacy Policy
                                </Link>
                            </label>
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white py-3 px-6 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg"
                        >
                            {isLoading ? (
                                <div className="flex items-center justify-center">
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                    Creating account...
                                </div>
                            ) : (
                                `Create ${formData.role === 'restaurant' ? 'Restaurant' : 'Customer'} Account`
                            )}
                        </button>

                        {/* QR Scanner Button for Customers */}
                        {formData.role === 'customer' && (
                            <div className="relative">
                                <div className="flex items-center my-4">
                                    <div className="flex-1 border-t border-white/20"></div>
                                    <span className="px-4 text-white/60 text-sm">or</span>
                                    <div className="flex-1 border-t border-white/20"></div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowScanner(true)}
                                    className="w-full bg-white/10 hover:bg-white/20 border-2 border-white/30 text-white py-3 px-6 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center justify-center space-x-2"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                                    </svg>
                                    <span>Scan Restaurant QR Code</span>
                                </button>
                                <p className="text-white/50 text-xs text-center mt-2">
                                    Skip registration and scan QR code at your table
                                </p>
                            </div>
                        )}
                    </form>
                </div>

                {/* Sign In Link */}
                <div className="text-center mt-6">
                    <p className="text-white/70">
                        Already have an account?{' '}
                        <Link to="/login" className="text-blue-400 hover:text-blue-300 font-semibold transition-colors">
                            Sign in here
                        </Link>
                    </p>
                </div>

                {/* Back to Home */}
                <div className="text-center mt-4">
                    <Link to="/" className="text-white/50 hover:text-white/70 text-sm transition-colors">
                        ← Back to Home
                    </Link>
                </div>
            </div>

            {/* QR Scanner Modal */}
            <QRScanner 
                isOpen={showScanner}
                onClose={() => setShowScanner(false)}
                onScan={handleQRScan}
            />
        </div>
    );
};

export default Register;
